import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../../contexts/AuthContext';
import { GET_TOPICS, GET_WORDS } from '../../graphql/queries';
import AIBattleWordsSuggester from './AIBattleWordsSuggester';

const PLAYER_NAME_KEY = 'flashcards_player_name';
// Room name is host-only metadata for telling sessions apart (e.g. "Period 1
// HSK 2 review"). It never reaches the backend and isn't shown to students.
const ROOM_NAME_KEY = 'flashcards_room_name';

/**
 * QuizLobby — Inkwell editorial battle UI.
 * Vermillion accent on room codes, monospace 4-character display, hairline rules.
 */
function QuizLobby({
  phase,
  roomCode,
  players,
  isHost,
  error,
  onCreateRoom,
  onJoinRoom,
  onStartGame,
  onLeave,
}) {
  // Students join their teacher's room; they never host. Default their name
  // to the one they enrolled with and skip the menu straight into the join form.
  const { sessionType, studentSession } = useAuth();
  const isStudent = sessionType === 'student';

  const [playerName, setPlayerName] = useState(
    () => studentSession?.studentName || localStorage.getItem(PLAYER_NAME_KEY) || ''
  );
  const [joinCode, setJoinCode] = useState('');
  const [roomName, setRoomName] = useState('');
  const [numQuestions, setNumQuestions] = useState(10);
  const [timeLimitSec, setTimeLimitSec] = useState(90);
  const [gameType, setGameType] = useState('text');
  const [selectedTopics, setSelectedTopics] = useState([]); // empty = all words
  const [view, setView] = useState(isStudent ? 'join' : 'menu'); // menu | create | join
  const [copied, setCopied] = useState(false);

  // Topics for the optional word filter — only the host's topics are shown.
  const { data: topicsData, refetch: refetchTopics } = useQuery(GET_TOPICS, { skip: isStudent });
  const availableTopics = topicsData?.topics || [];

  // Existing word hanzi — passed to the AI battle-words suggester so it can
  // skip duplicates. Skipped entirely for students (they don't host).
  const { data: wordsData, refetch: refetchWords } = useQuery(GET_WORDS, {
    skip: isStudent,
    fetchPolicy: 'cache-and-network',
  });
  const existingHanzi = (wordsData?.words || []).map((w) => w.hanzi);

  const handleSessionTopic = (topic) => {
    // Auto-select the per-session battle topic so the upcoming room uses
    // exactly the words just added, and refetch topics so the chip appears.
    setSelectedTopics((prev) => (prev.includes(topic) ? prev : [...prev, topic]));
    refetchTopics();
    refetchWords();
  };

  const toggleTopic = (topic) => {
    setSelectedTopics((prev) =>
      prev.includes(topic) ? prev.filter((t) => t !== topic) : [...prev, topic]
    );
  };

  const handlePlayerNameChange = (value) => {
    setPlayerName(value);
    localStorage.setItem(PLAYER_NAME_KEY, value);
  };

  const handleCopyRoomCode = () => {
    if (!roomCode) return;
    navigator.clipboard.writeText(roomCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleCreate = (e) => {
    e.preventDefault();
    // Persist the room name so the lobby + observer dashboard can read it back.
    // Cleared on Create so a stale name from a previous session doesn't leak in.
    const trimmed = roomName.trim();
    if (trimmed) {
      localStorage.setItem(ROOM_NAME_KEY, trimmed);
    } else {
      localStorage.removeItem(ROOM_NAME_KEY);
    }
    // Hosts are observers — playerName is irrelevant on the wire and the
    // backend ignores it. Pass empty so the hook serialises a valid payload.
    onCreateRoom('', numQuestions, timeLimitSec, gameType, selectedTopics);
  };

  const handleJoin = (e) => {
    e.preventDefault();
    if (playerName.trim() && joinCode.trim()) {
      onJoinRoom(playerName.trim(), joinCode.trim());
    }
  };

  const Seg = ({ value, options, onChange, render }) => (
    <div
      style={{
        display: 'flex',
        gap: 0,
        border: '1px solid var(--rule)',
        borderRadius: 8,
        overflow: 'hidden',
        background: 'var(--card)',
      }}
    >
      {options.map((o, i) => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          style={{
            flex: 1,
            height: 44,
            border: 0,
            background: value === o ? 'var(--ink)' : 'transparent',
            color: value === o ? 'var(--paper)' : 'var(--ink-2)',
            borderLeft: i ? '1px solid var(--rule)' : 0,
            fontWeight: 500,
            fontSize: 14,
            cursor: 'pointer',
            fontFamily: 'var(--font-sans)',
          }}
        >
          {render ? render(o) : o}
        </button>
      ))}
    </div>
  );

  // ── Active lobby (phase === 'lobby')
  if (phase === 'lobby') {
    return (
      <div className="theme-battle" style={{ minHeight: '100vh', background: 'var(--paper)' }}>
        <header className="ink-header">
          <button className="btn btn--ghost btn--sm" onClick={onLeave}>
            ← Leave room
          </button>
          <span className="t-mono" style={{
            fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)',
          }}>
            Lobby
          </span>
          <span className="badge" style={{ background: 'transparent', color: 'var(--ink-3)' }}>
            {numQuestions} questions · {timeLimitSec}s · {gameType === 'picture' ? 'Picture' : 'Hanzi'}
          </span>
        </header>

        <div style={{ maxWidth: 720, margin: '0 auto', padding: '48px 32px' }}>
          {(() => {
            const saved = localStorage.getItem(ROOM_NAME_KEY);
            if (!saved) return null;
            return (
              <h1 className="t-h2" style={{ textAlign: 'center', marginBottom: 24 }}>
                {saved}
              </h1>
            );
          })()}
          <div className="t-eyebrow" style={{ marginBottom: 12, textAlign: 'center' }}>
            Room code
          </div>
          <div
            className="qb-room-code"
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 96,
              letterSpacing: '0.18em',
              textAlign: 'center',
              padding: '24px 0',
              marginBottom: 16,
              border: '2px dashed var(--rule)',
              borderRadius: 12,
              color: 'var(--vermillion)',
            }}
          >
            {roomCode}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 8 }}>
            <button className="btn btn--ghost" onClick={handleCopyRoomCode}>
              {copied ? '✓ Copied' : 'Copy code'}
            </button>
          </div>
          <p className="t-small" style={{ textAlign: 'center', marginBottom: 32 }}>
            Share this code with your students. They join from your class link or via <strong className="t-mono">/join</strong>.
          </p>

          <div className="card" style={{ padding: 24 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'baseline', marginBottom: 16,
            }}>
              <h2 className="t-h3">Students</h2>
              <span className="t-small">{players.length} {players.length === 1 ? 'student' : 'students'}</span>
            </div>
            {players.length === 0 ? (
              <p className="t-small" style={{ textAlign: 'center', padding: '20px 0' }}>
                Waiting for students to join…
              </p>
            ) : (
              <div>
                {players.map((p, i) => (
                  <div
                    key={p.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '12px 0',
                      borderTop: i ? '1px solid var(--rule-2)' : 'none',
                    }}
                  >
                    <div style={{
                      width: 32, height: 32, borderRadius: 999,
                      background: 'var(--paper-2)', color: 'var(--ink-2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: 13,
                    }}>
                      {(p.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <span style={{ flex: 1, fontWeight: 400 }}>{p.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && (
            <p style={{ color: 'var(--error)', textAlign: 'center', marginTop: 16, fontSize: 13 }}>
              {error}
            </p>
          )}

          <div style={{ marginTop: 24 }}>
            {isHost ? (
              <button
                className="btn btn--accent btn--lg btn--block"
                onClick={onStartGame}
                disabled={players.length < 1}
              >
                {players.length < 1
                  ? 'Waiting for students to join…'
                  : `Start game · ${players.length} ${players.length === 1 ? 'student' : 'students'} ready`}
              </button>
            ) : (
              <div style={{
                padding: 16, background: 'var(--paper-2)',
                borderRadius: 12, textAlign: 'center',
              }}>
                <span className="pulse-dot" style={{
                  display: 'inline-block', width: 8, height: 8, borderRadius: 999,
                  background: 'var(--vermillion)', marginRight: 8, verticalAlign: 'middle',
                }} />
                <span className="t-small">Waiting for host to start…</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Menu
  if (view === 'menu') {
    return (
      <div className="theme-battle" style={{ minHeight: '100vh', background: 'var(--paper)' }}>
        <header className="ink-header" style={{ borderBottom: '1px solid var(--rule)' }}>
          <button className="btn btn--ghost btn--sm" onClick={onLeave}>← Modes</button>
          <span className="t-mono" style={{
            fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)',
          }}>
            Battle · 对战
          </span>
          <div style={{ width: 80 }} />
        </header>

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: 'calc(100vh - 64px)', padding: 40,
        }}>
          <div style={{ textAlign: 'center', maxWidth: 560 }}>
            <div style={{
              fontFamily: 'var(--font-han)', fontSize: 200, lineHeight: 0.9,
              marginBottom: 24, color: 'var(--vermillion)',
            }}>
              战
            </div>
            <h1 className="t-display" style={{ fontSize: 48, marginBottom: 12 }}>Quiz Battle</h1>
            <p className="t-body" style={{ fontSize: 17, marginBottom: 40 }}>
              Challenge your students to a Chinese-character showdown.
            </p>

            {error && (
              <p style={{ color: 'var(--error)', marginBottom: 16, fontSize: 13 }}>{error}</p>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              {!isStudent && (
                <button
                  className="btn btn--accent btn--lg"
                  style={{ minWidth: 200 }}
                  onClick={() => setView('create')}
                >
                  Create room
                </button>
              )}
              <button
                className={isStudent ? 'btn btn--accent btn--lg' : 'btn btn--lg'}
                style={isStudent ? { minWidth: 200 } : {
                  minWidth: 200, background: 'transparent',
                  color: 'var(--ink)', borderColor: 'var(--ink)',
                }}
                onClick={() => setView('join')}
              >
                Join room
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Create room form
  if (view === 'create') {
    return (
      <div className="theme-battle" style={{ minHeight: '100vh', background: 'var(--paper)' }}>
        <header className="ink-header">
          <button className="btn btn--ghost btn--sm" onClick={() => setView('menu')}>← Back</button>
          <span className="t-mono" style={{
            fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)',
          }}>
            Create room
          </span>
          <div style={{ width: 80 }} />
        </header>

        <div style={{ maxWidth: 540, margin: '0 auto', padding: '48px 32px' }}>
          <div className="t-eyebrow" style={{ marginBottom: 12 }}>New quiz battle</div>
          <p className="t-small" style={{ marginBottom: 24 }}>
            You'll host as observer — students join with their own names from the class link.
          </p>

          <form onSubmit={handleCreate}>
            <div className="field" style={{ marginBottom: 20 }}>
              <label className="field-label">Room name <span className="t-small">· optional</span></label>
              <input
                className="input"
                value={roomName}
                onChange={(e) => setRoomName(e.target.value.slice(0, 60))}
                placeholder="e.g. Period 1 — HSK 2 review"
                maxLength={60}
                autoFocus
              />
            </div>

            <div className="field" style={{ marginBottom: 20 }}>
              <label className="field-label">Game type</label>
              <Seg
                value={gameType}
                options={['text', 'picture']}
                onChange={setGameType}
                render={(o) => (o === 'text' ? 'Hanzi' : 'Picture')}
              />
            </div>

            {!isStudent && (
              <AIBattleWordsSuggester
                defaultHSKLevel={1}
                existingHanzi={existingHanzi}
                onSessionTopic={handleSessionTopic}
              />
            )}

            {availableTopics.length > 0 && (
              <div className="field" style={{ marginBottom: 20 }}>
                <label className="field-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span>Topics <span className="t-small">· optional</span></span>
                  {selectedTopics.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedTopics([])}
                      style={{
                        background: 'transparent',
                        border: 0,
                        color: 'var(--ink-3)',
                        fontSize: 12,
                        cursor: 'pointer',
                        fontFamily: 'var(--font-sans)',
                      }}
                    >
                      Clear
                    </button>
                  )}
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {availableTopics.map((topic) => (
                    <button
                      key={topic}
                      type="button"
                      className={`chip ${selectedTopics.includes(topic) ? 'chip--active' : ''}`}
                      onClick={() => toggleTopic(topic)}
                    >
                      {topic}
                    </button>
                  ))}
                </div>
                <p className="t-small" style={{ marginTop: 8, color: 'var(--ink-3)', fontSize: 12 }}>
                  {selectedTopics.length === 0
                    ? 'No filter — all words eligible.'
                    : `Drawing from: ${selectedTopics.join(', ')}.`}
                </p>
              </div>
            )}

            <div className="field" style={{ marginBottom: 20 }}>
              <label className="field-label">Questions</label>
              <Seg value={numQuestions} options={[5, 10, 15, 20]} onChange={setNumQuestions} />
            </div>

            <div className="field" style={{ marginBottom: 32 }}>
              <label className="field-label">Time limit</label>
              <Seg
                value={timeLimitSec}
                options={[60, 90, 120, 180]}
                onChange={setTimeLimitSec}
                render={(o) => `${o}s`}
              />
            </div>

            {error && (
              <p style={{ color: 'var(--error)', marginBottom: 16, fontSize: 13 }}>{error}</p>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                type="button"
                className="btn btn--ghost"
                style={{ flex: 1 }}
                onClick={() => setView('menu')}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn--accent"
                style={{ flex: 2 }}
              >
                Create room →
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ── Join form
  return (
    <div className="theme-battle" style={{ minHeight: '100vh', background: 'var(--paper)' }}>
      <header className="ink-header">
        <button
          className="btn btn--ghost btn--sm"
          onClick={isStudent ? onLeave : () => setView('menu')}
        >
          ← {isStudent ? 'Modes' : 'Back'}
        </button>
        <span className="t-mono" style={{
          fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)',
        }}>
          Join room
        </span>
        <div style={{ width: 80 }} />
      </header>

      <div style={{ maxWidth: 540, margin: '0 auto', padding: '48px 32px' }}>
        <div className="t-eyebrow" style={{ marginBottom: 12 }}>Join</div>
        <h1 className="t-h1" style={{ marginBottom: 32 }}>Existing room</h1>

        <form onSubmit={handleJoin}>
          <div className="field" style={{ marginBottom: 20 }}>
            <label className="field-label">Your name</label>
            <input
              className="input"
              value={playerName}
              onChange={(e) => handlePlayerNameChange(e.target.value)}
              placeholder="Enter your name"
              maxLength={20}
              autoFocus
            />
          </div>

          <div className="field" style={{ marginBottom: 32 }}>
            <label className="field-label">Room code</label>
            <input
              className="input input--lg input--mono"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="ABCD"
              maxLength={4}
              style={{ textAlign: 'center', fontSize: 24 }}
            />
          </div>

          {error && (
            <p style={{ color: 'var(--error)', marginBottom: 16, fontSize: 13 }}>{error}</p>
          )}

          <div style={{ display: 'flex', gap: 12 }}>
            <button
              type="button"
              className="btn btn--ghost"
              style={{ flex: 1 }}
              onClick={() => setView('menu')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--accent"
              style={{ flex: 2 }}
              disabled={!playerName.trim() || joinCode.length !== 4}
            >
              Join game →
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default QuizLobby;
